import * as AudioCategory from "../Audio/AudioCategory";
import { AudioToken } from "../Audio/AudioToken";
import * as Topics from "../Data/Topics";
import { OutlineFilter } from "../Filters/OutlineFilter";
import { IFocusable } from "../Input/IFocusable";
import * as InputUtils from "../Input/InputUtils";
import { ISelectable } from "../Input/ISelectable";
import * as PixiUtils from "../Utils/PixiUtils";
import * as RectUtils from "../Utils/RectUtils";

/**
 * Selectable
 */
export abstract class Selectable extends PIXI.Container implements ISelectable, IFocusable {
    public readonly onSelected: Array<(p: ISelectable) => void>;
    public readonly onDeselected: Array<(p: ISelectable) => void>;

    protected _isSelected: boolean;
    protected _visuals: PIXI.Container;
    protected _eventData: PIXI.interaction.InteractionData | undefined;
    protected _outlineFilter: OutlineFilter;
    protected _isFocussed: boolean;
    protected _hoverVo: string | undefined;
    protected _clickedSfx: string | undefined;

    constructor() {
        super();

        this._isFocussed = false;
        this._isSelected = false;

        this._visuals = new PIXI.Container();
        this.addChild(this._visuals);

        this._outlineFilter = new OutlineFilter();
        this._outlineFilter.uniforms.thickness = [0.025, 0.025];
        this._outlineFilter.uniforms.outlineColor = [70 / 255, 130 / 255, 210 / 255, 1];
        this._outlineFilter.uniforms.filterClamp = [0, 0, 1, 1];

        this.onSelected = new Array<(p: ISelectable) => void>();
        this.onDeselected = new Array<(p: ISelectable) => void>();

        this.on(InputUtils.Events.POINTER_OVER, this.onPointerOver);
        this.on(InputUtils.Events.POINTER_DOWN, this.onPointerDown);
        this.on(InputUtils.Events.POINTER_UP, this.onPointerUp);
        this.on(InputUtils.Events.POINTER_UP_OUTSIDE, this.onPointerUpOutside);
        this.on(InputUtils.Events.POINTER_OUT, this.onPointerOut);

        this.interactive = true;
        this.buttonMode = true;
        this.hitArea = new PIXI.Rectangle(-25, -25, 50, 50);
    }

    /**
     * Gets whether is selected
     */
    public get isSelected(): boolean {
        return this._isSelected;
    }

    /**
     * Selects selectable
     */
    public select(): void {
        this._isSelected = true;
        this._visuals.filters = [ this._outlineFilter ];
        for (let i = 0; i < this.onSelected.length; ++i) {
            this.onSelected[i](this);
        }
    }

    /**
     * Deselects selectable
     */
    public deselect(): void {
        this._isSelected = false;
        this._visuals.filters = [];
        for (let i = 0; i < this.onDeselected.length; ++i) {
            this.onDeselected[i](this);
        }
    }

    /**
     * Toggles selected
     */
    public toggleSelected(): void {
        if (this.isSelected) {
            this.deselect();
        }
        else {
            this.select();
        }
    }

    /**
     * onFocusBegin
     */
    public onFocusBegin(): void {
        this.playHoverVo();
    }

    /**
     * onFocusEnd
     */
    public onFocusEnd(): void {
        // unused
    }

    /**
     * onFocusActivated
     */
    public onFocusActivated(): void {
        this.toggleSelected();
        this.playClickedSFX();
    }

    /**
     * Gets focus position
     * @returns PIXI.Point
     */
    public getFocusPosition(): PIXI.Point {
        if (this.hitArea instanceof PIXI.Rectangle) {
            return this.toGlobal(RectUtils.center(this.hitArea));
        }
        else {
            return this.getGlobalPosition();
        }
    }

    /**
     * Gets focus size
     * @returns PIXI.Point
     */
    public getFocusSize(): PIXI.Point {
        let bounds: PIXI.Rectangle;
        if (this.hitArea instanceof PIXI.Rectangle) {
            bounds = PixiUtils.getGlobalBounds(this, this.hitArea.clone());
        }
        else {
            bounds = PixiUtils.getGlobalBounds(this);
        }
        return RectUtils.size(bounds);
    }

    /**
     * Plays hover vo
     */
    protected playHoverVo(): void {
        if (this._hoverVo !== undefined) {
            PubSub.publishSync(Topics.PLAY_AUDIO, new AudioToken(
                this._hoverVo, 1, false, AudioCategory.VO.toString(),
            ));
        }
    }

    /**
     * playClickedSFX
     */
    protected playClickedSFX(): void {
        if (this._clickedSfx !== undefined) {
            PubSub.publishSync(Topics.PLAY_AUDIO, new AudioToken(
                this._clickedSfx, 1, false, AudioCategory.SFX.toString(),
            ));
        }
    }

    /**
     * onPointerOver
     */
    protected onPointerOver(pEvent: PIXI.interaction.InteractionEvent): void {
        this._visuals.scale.set(1.05);
        this.playHoverVo();
    }

    /**
     * onPointerDown
     */
    protected onPointerDown(pEvent: PIXI.interaction.InteractionEvent): void {
        this._eventData = pEvent.data;
        this._visuals.scale.set(0.95);
    }

    /**
     * onPointerUp
     */
    protected onPointerUp(pEvent: PIXI.interaction.InteractionEvent): void {
        if (this._eventData !== undefined && this._eventData.identifier === pEvent.data.identifier) {
            this.toggleSelected();
            this._eventData = undefined;
            this._visuals.scale.set(1.05);
            this.playClickedSFX();
        }
    }

    /**
     * onPointerUpOutside
     */
    protected onPointerUpOutside(pEvent: PIXI.interaction.InteractionEvent): void {
        this._eventData = undefined;
    }

    /**
     * onPointerOut
     */
    protected onPointerOut(pEvent: PIXI.interaction.InteractionEvent): void {
        this._visuals.scale.set(1.0);
    }
}
